import { TOKEN } from 'constants/localStorage';

export const openerWindow = (): Window | null => {
  try {
    return (window.opener as Window | null) ?? null;
  } catch {
    return null;
  }
};

const childWindows = new Set<Window>();

export const registerChildWindow = (child: Window | null): void => {
  if (child) childWindows.add(child);
};

const isKnownChild = (source: MessageEventSource | null): boolean => {
  if (childWindows.size === 0) return true;
  for (const child of childWindows) {
    if (child === source) return true;
  }
  return false;
};

export const postToOpener = (message: Record<string, unknown> & { type: string }): void => {
  const opener = openerWindow();
  if (!opener) return;
  try {
    opener.postMessage(message, window.location.origin);
  } catch {
    return undefined;
  }
};

export const listenForChildMessage = (
  type: string,
  onMessage: (data: Record<string, unknown>, source: MessageEventSource | null) => void,
): (() => void) => {
  const handler = (event: MessageEvent): void => {
    if (event.origin !== window.location.origin) return;
    if (!isKnownChild(event.source)) return;
    const data = event.data as Record<string, unknown> | null;
    if (!data || data.type !== type) return;
    onMessage(data, event.source);
  };
  window.addEventListener('message', handler);
  return () => { window.removeEventListener('message', handler); };
};

const SIGN_OUT = 'mspp.callscribe.auth.signout';

export const broadcastSignOut = (): void => {
  for (const child of childWindows) {
    try {
      child.postMessage({ type: SIGN_OUT }, window.location.origin);
    } catch {
      continue;
    }
  }
};

export const listenForSignOut = (onSignOut: () => void): (() => void) => {
  const opener = openerWindow();
  if (!opener) return () => undefined;

  const handler = (event: MessageEvent): void => {
    if (event.origin !== window.location.origin) return;
    if (event.source !== opener) return;
    const data = event.data as { type?: string } | null;
    if (!data || data.type !== SIGN_OUT) return;
    onSignOut();
  };
  window.addEventListener('message', handler);
  return () => { window.removeEventListener('message', handler); };
};

export const listenForSharedSignOut = (onSignOut: () => void): (() => void) => {
  const handler = (event: StorageEvent): void => {
    if (event.storageArea !== window.localStorage) return;
    if (event.key !== null && (event.key !== TOKEN || event.newValue !== null)) return;
    onSignOut();
  };
  window.addEventListener('storage', handler);
  return () => { window.removeEventListener('storage', handler); };
};
